import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styles from "./reportsPresenter.module.scss";
import { useResize } from "ecom-teams-apps-core";
import pl from "shared/messages/pl";
import {
    Alert,
    ComponentEventHandler,
    Flex,
    FlexItem,
    MenuProps,
    Dropdown,
    DropdownItemProps,
} from "@fluentui/react-northstar";
import Tabs from "tabs/Tabs";
import { constants } from "shared/constants";
import { Tab } from "shared/models/Tab";
import pages from "shared/routing/pages";
import { useNavigate } from "react-router-dom";
import { ScorecardReportV3Filter_PublicLevel, ScorecardV3_Public } from "shared/service/api/generated";
import { ScoreReportType } from "./store/ScoreReportType";
import ScoreCardPage from "./tabs/ScoreCardReport/ScoreCardPage";
import { useStore } from "stores/Stores";
import { BusinessRole } from "shared/enums/BusinessRole";

export interface PeriodsDropdownItem {
    key: string;
    header: string;
    selected: boolean;
}
export interface ReportTypeWithPeriods {
    tab: Tab;
    scoreReportType: ScoreReportType;
    periods: ScorecardV3_Public[];
}

export interface ReportsPresenterProps {
    availableReportsWithPeriods: ReportTypeWithPeriods[];
    activeTab: ScoreReportType;
}

export interface ActiveReportWithPeriod {
    reportType?: ScoreReportType;
    period?: ScorecardV3_Public;
}

const ReportsPresenter: React.FC<ReportsPresenterProps> = observer((props: ReportsPresenterProps) => {
    const windowRef = useRef(document.body);
    const [windowWidth] = useResize(windowRef);
    const { authorizationStore } = useStore();
    const isMobile = windowWidth < constants.mediumDeviceWidthPx;
    const navigation = useNavigate();

    const [activeTabAndPeriod, setActiveTabAndPeriod] = useState<ActiveReportWithPeriod>();

    useEffect(() => {
        var activeTab = props.availableReportsWithPeriods.find((p) => p.scoreReportType === props.activeTab);
        if (!activeTab) {
            return;
        }

        setActiveTabAndPeriod({
            reportType: activeTab.scoreReportType,
            period: activeTab.periods.length > 0 ? activeTab.periods[0] : undefined,
        });
    }, [props.activeTab, props.availableReportsWithPeriods]);

    const handleOnActiveIndexChange: ComponentEventHandler<MenuProps> = (_, data) => {
        if (data?.activeIndex === undefined) {
            return;
        }
        const activeIndex = Number(data.activeIndex);
        const switchingTo = props.availableReportsWithPeriods[activeIndex];

        let navigateTo = pages.goalsMonthly.path;

        switch (switchingTo.scoreReportType) {
            case ScoreReportType.MONTHLY_RMS:
                navigateTo = pages.goalsMonthlyRms.path;
                break;
            case ScoreReportType.QUARTERLY:
                navigateTo = pages.goalsQuarterly.path;
                break;
            case ScoreReportType.MONTHLY:
            default:
                break;
        }

        navigation(navigateTo);
    };

    const getDropDownPeriodItems = (): PeriodsDropdownItem[] => {
        if (!activeTabAndPeriod) {
            return [];
        }

        const periods = props.availableReportsWithPeriods.find(
            (i) => i.scoreReportType === activeTabAndPeriod.reportType
        )?.periods;
        if (!periods) {
            return [];
        }

        return periods.map((item) => {
            return {
                key: item.token ?? "",
                header: item.description ?? "",
                selected: item.token === activeTabAndPeriod.period?.token,
            };
        });
    };

    const getDropDownCurrentPeriodValue = (): DropdownItemProps | undefined => {
        if (!activeTabAndPeriod) {
            return undefined;
        }
        return getDropDownPeriodItems().find((i) => i.key === activeTabAndPeriod.period!.token);
    };

    const handleOnActivePeriodDropDownChange = (value: PeriodsDropdownItem) => {
        const currentReportType = activeTabAndPeriod!.reportType;
        const swithingTo = props.availableReportsWithPeriods
            .find((i) => i.scoreReportType === currentReportType)
            ?.periods.find((p) => p.token === value.key);

        setActiveTabAndPeriod({
            reportType: currentReportType,
            period: swithingTo!,
        });
    };

    const renderError = (errorMessage: string) => {
        return (
            <Flex column padding="padding.medium">
                <FlexItem align="start">
                    <Alert danger content={errorMessage} styles={{ padding: "6px 8px" }} />
                </FlexItem>
            </Flex>
        );
    };

    const renderDropdown = () => (
        <Dropdown
            items={getDropDownPeriodItems()}
            checkable
            fluid
            value={getDropDownCurrentPeriodValue()}
            inverted
            onChange={(_, dropdown) => handleOnActivePeriodDropDownChange(dropdown.value as PeriodsDropdownItem)}
        />
    );

    const renderReportContents = () => {
        const scoreCardPeriod = activeTabAndPeriod?.period;

        const isHq = authorizationStore.currentUser?.businessRole === BusinessRole.HQ;
        let level = undefined;

        if (isHq) {
            if (activeTabAndPeriod?.reportType === ScoreReportType.MONTHLY_RMS) {
                level = ScorecardReportV3Filter_PublicLevel.MS;
            } else {
                level = ScorecardReportV3Filter_PublicLevel.DP;
            }
        }
        if (scoreCardPeriod) {
            return (
                <ScoreCardPage
                    key={`${level}_${scoreCardPeriod.token}`}
                    level={level}
                    scoreCardPeriod={scoreCardPeriod}
                />
            );
        } else {
            return <>Nothing to show</>;
        }
    };

    if (!activeTabAndPeriod || windowWidth === 0) {
        return null;
    }

    return (
        <>
            <Flex>
                <div className={styles.reportsWrapper} style={{ width: "100%" }}>
                    <Tabs
                        additionalClassName={styles.tabs}
                        activeTab={props.availableReportsWithPeriods.findIndex(
                            (i) => i.scoreReportType === activeTabAndPeriod.reportType
                        )}
                        items={props.availableReportsWithPeriods.map((i) => i.tab)}
                        onActiveIndexChange={handleOnActiveIndexChange}
                    />
                    {isMobile && (
                        <div style={{ width: "200px" }}>
                            <div>{renderDropdown()}</div>
                        </div>
                    )}
                    {props.availableReportsWithPeriods.length === 0
                        ? renderError(pl.reports.noReports)
                        : renderReportContents()}
                </div>
                {!isMobile && (
                    <div
                        style={{
                            position: "absolute",
                            right: "16px",
                            paddingTop: "0.45rem",
                        }}
                    >
                        {renderDropdown()}
                    </div>
                )}
            </Flex>
        </>
    );
});

export default ReportsPresenter;
